import { TimesheetWarnings } from './TimesheetWarnings';
import { useEffect, useState } from "react";
import {Link} from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { AuthImage } from "@/components/AuthImage";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import "./TimesheetInspectionPanel.css";

function timestamp(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString("en-NZ", {
    day: "numeric", month: "short", hour: "numeric", minute: "2-digit",
  });
}

function Fact({ label, children, emphasis = false, numeric = false }) {
  if (children === null || children === undefined || children === "") return null;
  return <div className={`ts-inspection-fact ${emphasis ? "ts-inspection-fact-strong" : ""}`}><dt className="text-zinc-500 dark:text-zinc-400 shrink-0">{label}</dt><dd className={`min-w-0 break-words ${numeric ? "font-mono tabular-nums" : ""} ${emphasis ? "text-base font-bold text-zinc-950 dark:text-white" : "font-medium"}`}>{children}</dd></div>;
}

function TravelBillingControl({ ts, onSaved }) {
  const { user } = useAuth();
  const [decision, setDecision] = useState(ts.travel_billing_decision || "pending");
  const [saving, setSaving] = useState(false);
  const [jobTravel, setJobTravel] = useState({loading: true, value: null});
  useEffect(() => {
    let current = true;
    setJobTravel({loading: true, value: null});
    if (!ts.job_id || !ts.travel_segments?.length || !["admin", "office", "super_admin"].includes(user?.role)) {
      setJobTravel({loading: false, value: null});
      return () => { current = false; };
    }
    api.get(`/jobs/${ts.job_id}`, {__flSkipReadCache: true}).then(({data}) => {
      if (current) setJobTravel({loading: false, value: data.bill_travel_time ?? null});
    }).catch(() => { if (current) setJobTravel({loading: false, value: null, error: true}); });
    return () => { current = false; };
  }, [ts.job_id, ts.travel_segments?.length, user?.role]);
  useEffect(() => setDecision(ts.travel_billing_decision || "pending"), [ts.id, ts.travel_billing_decision]);
  if (!ts.travel_segments?.length || !["admin", "office", "super_admin"].includes(user?.role)) return null;
  if (jobTravel.loading || jobTravel.error) return <p className="text-xs text-zinc-500">{jobTravel.loading ? "Loading job travel billing…" : "Could not load job travel billing. Reopen this timesheet to retry."}</p>;
  if (jobTravel.value !== null) return <p className="my-3 text-xs text-zinc-500">Travel billing: {jobTravel.value ? "Chargeable" : "Not chargeable"} — controlled in Job billing settings. Recorded and payroll hours are unchanged.{ts.invoiced_invoice_id && " Existing invoice lines remain unchanged."}</p>;
  const save = async (value) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/timesheets/${ts.id}/travel-billing`, { decision: value });
      setDecision(data.travel_billing_decision);
      toast.success("Travel billing decision saved");
      onSaved?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save travel billing decision");
    } finally { setSaving(false); }
  };
  return <div className="my-3 border-t border-zinc-200 pt-3" data-testid={`ts-travel-billing-${ts.id}`}>
    <label className="block text-xs font-semibold">Travel — client billing
      <select value={decision} disabled={saving || !!ts.invoiced_invoice_id} onChange={e => save(e.target.value)} className="mt-2 block w-full min-h-11 rounded-sm border border-zinc-400 bg-white text-zinc-900 px-2">
        <option value="pending">Awaiting office decision</option>
        <option value="billable">Charge client</option>
        <option value="non_billable">Do not charge client</option>
      </select>
    </label>
    <p className="mt-1 text-[11px] text-zinc-500">Charge client makes approved travel available in Job Billing using existing labour rates. Payroll hours stay the same.</p>
    {ts.invoiced_invoice_id && <p className="text-[11px]">Billing decision locked after invoicing.</p>}
  </div>;
}

export function TimesheetInspectionPanel({ ts, onBillingChange }) {
  const isWork = !ts.entry_type || ts.entry_type === "work";
  // Office display only; payable hours remain the API's existing hours value.
  const workedHours = ts.hours ?? 0;
  const summary = ts.civil_allocation_summary;
  const allocation = ts.civil_allocation || summary?.allocation;
  const allocationStatus = ts.civil_allocation ? "recorded" : summary?.status || "review_required";
  const plantHours = allocation?.plant_hours ?? ts.fleet_hours ?? 0;
  const labourHours = allocation?.labour_hours;
  const plantExceedsWorked = plantHours > workedHours;
  const tasks = Array.isArray(ts.task_hours_detail) ? ts.task_hours_detail : [];
  const assets = Array.isArray(ts.assets) ? ts.assets : [];
  const gear = Array.isArray(ts.gear_items) ? ts.gear_items : [];
  const photos = (ts.photo_paths || []).filter((p) => !p.startsWith("offline://"));
  const hasWork = ts.job_title || ts.job_number || ts.job_site_address || ts.cost_code_name || ts.cost_code_code || tasks.length || ts.notes || photos.length || ts.source === "truck_dispatch";
  const statusColor = {
    approved: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    pending: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
    rejected: "bg-red-500/10 text-red-700 dark:text-red-400",
  }[ts.status] || "text-zinc-600 dark:text-zinc-300";
  const heading = "text-[10px] font-bold uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mb-2";

  return (
    <div id={`ts-inspection-${ts.id}`} className="ts-inspection text-xs leading-5 text-zinc-700 dark:text-zinc-200" aria-label={`Timesheet inspection for ${ts.user_name || ts.date}`}>
      {!!ts.warnings?.length && <div className="col-span-full"><TimesheetWarnings warnings={ts.warnings} detail /></div>}
      <section className="min-w-0">
        <h3 className={heading}>Time &amp; Pay</h3>
        {isWork && (!ts.travel_segments?.length || ts.work_hours > 0) && <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-2">
          <div className="flex items-center gap-2 font-mono tabular-nums font-semibold text-zinc-800 dark:text-zinc-100">
            {ts.start_time && <span aria-label={`Start ${ts.start_time}`}>{ts.start_time}</span>}
            {ts.start_time && ts.end_time && <span aria-hidden="true" className="font-normal text-zinc-400">→</span>}
            {ts.end_time && <span aria-label={`Finish ${ts.end_time}`}>{ts.end_time}</span>}
          </div>
          <span className="text-[11px] text-zinc-500 dark:text-zinc-400">Break <span className="font-mono tabular-nums">{ts.break_minutes || 0} min</span></span>
        </div>}
        {!!ts.travel_segments?.length && <div className="mb-3 space-y-1">
          <p className="font-semibold">Working {(ts.work_hours || 0).toFixed(2)} h · Travel {(ts.travel_hours || 0).toFixed(2)} h</p>
          {ts.travel_segments.map((segment, i) => <p key={i}>Travel {segment.start_time}–{segment.end_time} · {(segment.hours || 0).toFixed(2)} h<br />{segment.from_location} → {segment.to_location}{segment.notes && ` · ${segment.notes}`}</p>)}
        </div>}
        <TravelBillingControl ts={ts} onSaved={onBillingChange} />
        <dl>
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">{isWork ? "Actual Worked" : "Recorded hours"}</dt>
            <dd className="font-mono tabular-nums text-xl leading-7 font-bold text-zinc-950 dark:text-white">{workedHours.toFixed(2)} <span className="text-sm font-medium">h</span></dd>
          </div>
        </dl>
        {ts.non_billable_hours > 0 && <p className="mt-3 text-xs font-medium">Non-billable labour: {Number(ts.non_billable_hours).toFixed(2)} h. Included in pay and job costs; excluded from customer labour billing.</p>}
        {isWork && <div className="mt-3" data-testid={`ts-hours-breakdown-${ts.id}`}>
          <h3 className={heading}>Job / Client Allocation</h3>
          {allocation ? <>
            <dl className="space-y-0.5">
              <div className="ts-inspection-plant-row font-semibold"><dt>Labour</dt><dd className="font-mono tabular-nums">{labourHours.toFixed(2)} h</dd></div>
              {assets.map(asset => <div key={asset.id} className="ts-inspection-plant-row"><dt>{asset.rego || [asset.make, asset.model].filter(Boolean).join(" ") || "Fleet plant"}</dt><dd className="font-mono tabular-nums">{asset.hours.toFixed(2)} h</dd></div>)}
              <div className="ts-inspection-plant-row"><dt>Total fleet allocation</dt><dd className="font-mono tabular-nums">{plantHours.toFixed(2)} h</dd></div>
              {allocation.travel_hours > 0 && <div className="ts-inspection-plant-row"><dt>Travel</dt><dd>{allocation.travel_hours.toFixed(2)} h</dd></div>}
              {summary?.total_hours != null && <div className="ts-inspection-plant-row border-t pt-1 font-semibold"><dt>Total allocated</dt><dd>{summary.total_hours.toFixed(2)} h</dd></div>}
            </dl>
            <span className="inline-block mt-2 rounded bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 text-[10px] font-semibold text-zinc-500">{allocationStatus === "recorded" ? "RECORDED" : "HISTORICAL DERIVED"}</span>
            {allocationStatus === "derived" && <p className="mt-1 text-[11px] text-zinc-500">Derived from the original worked time and recorded fleet usage.</p>}
          </> : <p className="font-medium">Allocation requires review. Known recorded usage is shown below.</p>}
          {allocation && ts.client_billable_labour_hours != null && <div className="mt-3"><h3 className={heading}>Client Billing</h3><p>Billable labour {ts.client_billable_labour_hours.toFixed(2)} h · Billable plant {ts.client_billable_plant_hours.toFixed(2)} h</p>{ts.invoiced_invoice_id && <p>Current allocation shown; locked invoice quantities remain unchanged.</p>}</div>}
          {plantExceedsWorked && <p role="alert" className="mt-1 text-[11px] font-medium leading-4 text-amber-700 dark:text-amber-300">Plant hours exceed worked hours</p>}
        </div>}
        {(assets.length > 0 || gear.length > 0 || (ts.fleet_hours || 0) > 0 || (ts.gear_hours_total || 0) > 0) && <div className="mt-3">
          <div className="flex items-center gap-1.5 mb-2">
            <h3 className={heading.replace("mb-2", "")}>Plant Used</h3>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" aria-label="About plant hours" className="rounded text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FF5F15]">
                    <Info className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-64 normal-case font-normal tracking-normal">Fleet allocation replaces site labour for client billing. Small equipment is additional usage. Neither changes payroll hours.</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <dl className="space-y-0.5">
            {assets.map((asset) => <div key={asset.id} className="ts-inspection-plant-row" data-testid={`ts-asset-detail-${ts.id}-${asset.id}`}>
              <dt className="min-w-0 break-words font-medium">{asset.rego || [asset.make, asset.model].filter(Boolean).join(" ") || "Unnamed plant"}</dt>
              <dd className="font-mono tabular-nums whitespace-nowrap">{typeof asset.hours === "number" ? `${asset.hours.toFixed(2)} h` : "—"}</dd>
            </div>)}
            {gear.map((item) => <div key={item.id} className="ts-inspection-plant-row">
              <dt className="min-w-0 break-words font-medium">{item.name}</dt>
              <dd className="font-mono tabular-nums whitespace-nowrap">{typeof item.hours === "number" ? `${item.hours.toFixed(2)} h` : "—"}</dd>
            </div>)}
            <div className="ts-inspection-plant-row border-t border-zinc-200/70 dark:border-zinc-700/60 pt-1 mt-1 font-semibold">
              <dt>Total Equipment Usage (including small equipment)</dt>
              <dd className="font-mono tabular-nums whitespace-nowrap text-[#FF5F15]">{`${(plantHours + (ts.gear_hours_total || 0)).toFixed(2)} h`}</dd>
            </div>
          </dl>
        </div>}
      </section>

      <section className="min-w-0">
        <h3 className={heading}>Job &amp; work</h3>
        {!hasWork ? <p className="text-zinc-500 dark:text-zinc-400">No job or work activity allocated</p> : <>
          {(ts.job_number || ts.job_title) && <div className="mb-1.5 text-zinc-900 dark:text-white">
            {ts.job_number && <span className="font-mono text-[11px] font-semibold">{ts.job_number}</span>}
            {ts.job_title && <p className="font-semibold leading-5 break-words">{ts.job_title}</p>}
          </div>}
          <dl className="space-y-0.5">
            <Fact label="Site">{ts.job_site_address}</Fact>
            <Fact label="Cost code">{[ts.cost_code_code, ts.cost_code_name].filter(Boolean).join(" · ")}</Fact>
            {ts.source === "truck_dispatch" && <>
              <Fact label="Source">{ts.trucking_job_id ? <Link className="underline" to={`/app/trucking/dispatch?date=${encodeURIComponent(ts.date)}#trucking-card-${encodeURIComponent(ts.trucking_job_id)}`}>Trucking · {[ts.trucking_job_number, ts.trucking_job_title].filter(Boolean).join(" · ") || "Open source run"}</Link> : "Truck dispatch"}</Fact>
              {ts.trucking_truck_rego && <Fact label="Truck">{ts.trucking_truck_rego}</Fact>}
              {(ts.run_started_at || ts.run_completed_at) && <Fact label="Recorded run">{[ts.run_started_at, ts.run_completed_at].map(value => value && !Number.isNaN(new Date(value).getTime()) ? new Date(value).toLocaleString("en-NZ", {timeZone:"Pacific/Auckland",day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"}) : "Not recorded").join(" → ")}</Fact>}
            </>}
            {tasks.map((task) => <Fact key={task.task_id} label="Task">{task.title} · {Number(task.hours || 0).toFixed(2)} h{task.machine_names?.length > 0 && ` · ${task.machine_names.join(", ")}`}</Fact>)}
          </dl>
          {ts.notes && <p className="mt-2 whitespace-pre-wrap break-words leading-5"><span className="block text-[11px] text-zinc-500 dark:text-zinc-400">Worker note</span>{ts.notes}</p>}
          {photos.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`ts-photos-${ts.id}`}>
            {photos.map((path) => <div key={path} className="h-12 w-12 overflow-hidden rounded border border-zinc-200 dark:border-zinc-700"><AuthImage path={path} alt="Job photo" className="h-full w-full object-cover" /></div>)}
          </div>}
        </>}
      </section>

      <section className="min-w-0">
        <h3 className={heading}>Review / approval</h3>
        <div className="mb-2 flex items-center gap-2">
          <span aria-label={`Status: ${ts.status}`} className={`inline-flex rounded px-2 py-1 text-[11px] font-bold uppercase tracking-wider ${statusColor}`}>{ts.status}</span>
          {ts.paid && <span className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">Paid</span>}
        </div>
        <dl className="space-y-1">
          <Fact label="Created by">{ts.created_by_name}</Fact>
          <Fact label={ts.resubmitted ? "Resubmitted" : "Submitted"}>{timestamp(ts.submitted_at)}</Fact>
          {!ts.submitted_at && <Fact label="Created">{timestamp(ts.created_at)}</Fact>}
          <Fact label="Reviewer">{ts.reviewer_name}</Fact>
          <Fact label="Reviewed">{timestamp(ts.reviewed_at)}</Fact>
          <Fact label="Edited">{timestamp(ts.updated_at)}</Fact>
        </dl>
        {ts.review_notes && <p className={`mt-1 whitespace-pre-wrap break-words ${ts.status === "rejected" ? statusColor : ""}`}><span className="font-semibold">{ts.status === "rejected" ? "Rejection reason" : "Review note"} · </span>{ts.review_notes}</p>}

      </section>
    </div>
  );
}
